import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, ShieldAlert, RefreshCw, VolumeX, Undo2, CheckCircle2, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';
import { useToast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

/**
 * ModerationPanel — review auto-flagged chat comments and manage mutes.
 * Restore puts the original uncensored text back and refunds one strike;
 * Unmute lifts an active mute immediately.
 */
const ModerationPanel = () => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [flagged, setFlagged] = useState([]);
  const [muted, setMuted] = useState([]);
  const [busy, setBusy] = useState(null);
  const [editing, setEditing] = useState(null); // { id, text }
  const token = localStorage.getItem('token');
  const auth = { headers: { Authorization: `Bearer ${token}` } };

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [f, m] = await Promise.all([
        axios.get(`${API}/chat/admin/flagged-messages`, auth),
        axios.get(`${API}/chat/muted-users`, auth),
      ]);
      setFlagged(f.data?.messages || []);
      setMuted(Array.isArray(m.data) ? m.data : []);
    } catch (e) {
      toast({
        title: 'Could not load moderation data',
        description: e.response?.data?.detail || e.message,
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); /* eslint-disable-next-line */ }, []);

  const restoreMessage = async (msg, editedText = null) => {
    setBusy(`restore-${msg.id}`);
    try {
      const body = editedText != null ? { content: editedText } : null;
      await axios.post(`${API}/chat/admin/restore-message/${msg.id}`, body, auth);
      toast({ title: 'Comment restored', description: 'The corrected text is now visible in the discussion.' });
      setEditing(null);
      await fetchAll();
    } catch (e) {
      toast({ title: 'Restore failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const deleteMessage = async (msg) => {
    if (!window.confirm('Permanently delete this comment? It will disappear from the chat and this list. This cannot be undone.')) return;
    setBusy(`delete-${msg.id}`);
    try {
      await axios.delete(`${API}/chat/admin/messages/${msg.id}`, auth);
      toast({ title: 'Comment deleted', description: 'The comment was permanently removed.' });
      setFlagged((prev) => prev.filter((f) => f.id !== msg.id));
    } catch (e) {
      toast({ title: 'Delete failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const unmuteUser = async (userId, name) => {
    setBusy(`unmute-${userId}`);
    try {
      await axios.post(`${API}/chat/unmute/${userId}`, null, auth);
      toast({ title: 'User unmuted', description: `${name || 'User'} can chat again (strikes reset).` });
      await fetchAll();
    } catch (e) {
      toast({ title: 'Unmute failed', description: e.response?.data?.detail || e.message, variant: 'destructive' });
    } finally {
      setBusy(null);
    }
  };

  const fmt = (iso) => {
    if (!iso) return '';
    try { return new Date(iso).toLocaleString(); } catch { return iso; }
  };

  return (
    <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-8" data-testid="moderation-panel">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-3">
          <ShieldAlert className="h-5 w-5 text-amber-400" />
          <div>
            <h2 className="text-lg font-semibold text-white">Chat Moderation</h2>
            <p className="text-sm text-gray-400">
              Review flagged comments, restore false positives, and lift mutes.
            </p>
          </div>
        </div>
        <Button
          variant="outline"
          size="sm"
          onClick={fetchAll}
          disabled={loading}
          data-testid="moderation-refresh-btn"
        >
          {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
        </Button>
      </div>

      {/* Muted users */}
      <div className="mb-6">
        <h3 className="text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
          <VolumeX className="h-4 w-4 text-red-400" /> Currently Muted ({muted.length})
        </h3>
        {muted.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="no-muted-users">No users are muted right now.</p>
        ) : (
          <div className="space-y-2">
            {muted.map((m) => (
              <div
                key={m.user_id}
                className="flex items-center justify-between gap-3 bg-white/5 rounded-lg px-4 py-3"
                data-testid={`muted-user-${m.user_id}`}
              >
                <div className="min-w-0">
                  <p className="text-sm text-white font-medium truncate">
                    {m.user_name || m.user_id} {m.user_email && <span className="text-gray-500 font-normal">({m.user_email})</span>}
                  </p>
                  <p className="text-xs text-gray-400 truncate">
                    {m.reason} · until {fmt(m.expires_at)}
                  </p>
                </div>
                <Button
                  size="sm"
                  className="bg-emerald-600 hover:bg-emerald-500 shrink-0"
                  onClick={() => unmuteUser(m.user_id, m.user_name)}
                  disabled={busy === `unmute-${m.user_id}`}
                  data-testid={`unmute-btn-${m.user_id}`}
                >
                  {busy === `unmute-${m.user_id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Unmute'}
                </Button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Flagged messages */}
      <div>
        <h3 className="text-sm font-semibold text-gray-300 mb-2 flex items-center gap-2">
          <ShieldAlert className="h-4 w-4 text-amber-400" /> Flagged Comments ({flagged.length})
        </h3>
        {flagged.length === 0 ? (
          <p className="text-sm text-gray-500" data-testid="no-flagged-messages">No flagged comments.</p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto pr-1">
            {flagged.map((f) => (
              <div key={f.id} className="bg-white/5 rounded-lg px-4 py-3" data-testid={`flagged-msg-${f.id}`}>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xs text-gray-400 truncate">
                    <span className="text-white font-medium">{f.user_name}</span>
                    {f.user_email ? ` (${f.user_email})` : ''} · {f.room_label} · {fmt(f.created_at)}
                    {f.user_is_muted && <span className="ml-2 text-red-400 font-semibold">MUTED</span>}
                    {f.deleted && <span className="ml-2 text-orange-400 font-semibold">DELETED</span>}
                  </p>
                  <div className="flex gap-2 shrink-0">
                    {f.user_is_muted && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => unmuteUser(f.user_id, f.user_name)}
                        disabled={busy === `unmute-${f.user_id}`}
                        data-testid={`flagged-unmute-btn-${f.id}`}
                      >
                        Unmute
                      </Button>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() =>
                        setEditing(editing?.id === f.id ? null : { id: f.id, text: f.original_content || f.content || '' })
                      }
                      data-testid={`edit-btn-${f.id}`}
                    >
                      <Pencil className="h-3.5 w-3.5" />
                    </Button>
                    <Button
                      size="sm"
                      className="bg-amber-600 hover:bg-amber-500"
                      onClick={() => restoreMessage(f)}
                      disabled={busy === `restore-${f.id}`}
                      data-testid={`restore-btn-${f.id}`}
                    >
                      {busy === `restore-${f.id}` ? (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      ) : (
                        <span className="flex items-center gap-1"><Undo2 className="h-3.5 w-3.5" /> Restore</span>
                      )}
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      className="border-red-500/40 text-red-400 hover:bg-red-500/15 hover:text-red-300"
                      onClick={() => deleteMessage(f)}
                      disabled={busy === `delete-${f.id}`}
                      title="Permanently delete this comment"
                      data-testid={`delete-btn-${f.id}`}
                    >
                      {busy === `delete-${f.id}` ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                </div>
                <p className="text-sm text-gray-300 mt-1.5">
                  <span className="text-gray-500 text-xs mr-1">Shown:</span> {f.content}
                </p>
                {f.original_content && (
                  <p className="text-sm text-emerald-300 mt-0.5">
                    <span className="text-gray-500 text-xs mr-1">Original:</span> {f.original_content}
                  </p>
                )}
                {!f.original_content && (
                  <p className="text-xs text-gray-500 mt-0.5 flex items-center gap-1">
                    <CheckCircle2 className="h-3 w-3" /> Original text not stored — use the pencil to type the corrected text (e.g. replace ****), then Save &amp; Restore.
                  </p>
                )}
                {editing?.id === f.id && (
                  <div className="mt-2 space-y-2" data-testid={`edit-area-${f.id}`}>
                    <textarea
                      value={editing.text}
                      onChange={(e) => setEditing({ id: f.id, text: e.target.value })}
                      rows={3}
                      className="w-full text-sm bg-black/30 border border-white/15 rounded-lg p-2.5 text-gray-100 focus:outline-none focus:border-amber-500/60"
                      data-testid={`edit-textarea-${f.id}`}
                    />
                    <div className="flex gap-2 justify-end">
                      <Button size="sm" variant="ghost" onClick={() => setEditing(null)} data-testid={`edit-cancel-${f.id}`}>
                        Cancel
                      </Button>
                      <Button
                        size="sm"
                        className="bg-emerald-600 hover:bg-emerald-500"
                        onClick={() => restoreMessage(f, editing.text)}
                        disabled={!editing.text.trim() || busy === `restore-${f.id}`}
                        data-testid={`edit-save-${f.id}`}
                      >
                        Save &amp; Restore
                      </Button>
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default ModerationPanel;
